/**
 * reCAPTCHA v3 integration — invisible, score-based bot protection.
 * Loads the script dynamically and provides a token for backend verification.
 * Gracefully degrades when REACT_APP_RECAPTCHA_SITE_KEY is not set (local dev).
 */
let loadPromise = null;

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function loadRecaptchaScript() {
  if (loadPromise) return loadPromise;
  if (!SITE_KEY) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(resolve);
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Get a reCAPTCHA v3 token for the given action.
 * Returns null if reCAPTCHA is not configured (local dev).
 */
export async function getRecaptchaToken(action) {
  if (!SITE_KEY) return null;

  try {
    await loadRecaptchaScript();
    const token = await window.grecaptcha.execute(SITE_KEY, { action });
    return token;
  } catch (err) {
    console.warn('[reCAPTCHA] Failed to get token:', err.message);
    return null;
  }
}

/**
 * Whether reCAPTCHA is enabled (site key is configured).
 */
export function isRecaptchaEnabled() {
  return !!SITE_KEY;
}
